import { X } from "lucide-react";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { DocumentChatSidebarTranslations } from "@/lib/i18n/types/documents-sections/chat";
import { cn } from "@/lib/utils";
import type { ChatNudge as ChatNudgeData } from "./chatNudgeStore";
import { FranklinSpeechBubble } from "./FranklinSpeechBubble";
import {
  FRANKLIN_UPDATE_CLOSE_CLASS_NAME,
  FRANKLIN_UPDATE_CLOSE_GUTTER_CLASS_NAME,
  FRANKLIN_UPDATE_PRIMARY_ACTION_CLASS_NAME,
  FRANKLIN_UPDATE_SECONDARY_ACTION_CLASS_NAME,
  FRANKLIN_UPDATE_TEXT_CLASS_NAME,
} from "./franklinUpdateVisual";

export interface ChatNudgeProps {
  nudge: ChatNudgeData;
  /** Open the chat and start the summary. Absent on status-only nudges. */
  onSummarise?: () => void;
  /** Navigate to the Issues page (new-issues nudge only). */
  onViewIssues?: () => void;
  /** Dismiss — silences this nudge type for the rest of the session. */
  onDismiss: () => void;
}

function nudgeMessage(
  nudge: ChatNudgeData,
  copy: DocumentChatSidebarTranslations["nudges"]
): string {
  const count = String(nudge.count);
  if (nudge.type === "processing_done") {
    return nudge.count === 1
      ? copy.processingDoneOne
      : copy.processingDone.replace("{count}", count);
  }
  return nudge.count === 1 ? copy.newIssuesOne : copy.newIssues.replace("{count}", count);
}

/**
 * A small card in the bottom-right lane, above the collapsed chat pill. Never
 * steals focus: it renders silently, has no autofocus, and its actions are
 * opt-in. The lane owns its placement — see `layout/BottomRightLane.tsx`.
 *
 * Same layout as every Franklin toast (`franklinUpdateVisual.ts`): message
 * beside the logo, actions on their own row beneath it, close control pinned
 * inside the top-right corner.
 */
export function ChatNudge({ nudge, onSummarise, onViewIssues, onDismiss }: ChatNudgeProps) {
  const { t } = useLanguage();
  const copy = t.documents.chatSidebar.nudges;

  return (
    <FranklinSpeechBubble
      sizing="fit"
      role="status"
      aria-live="polite"
      data-testid={`chat-nudge-${nudge.type}`}
    >
      <div className="flex items-start gap-2.5">
        <FranklinLogo size="xs" showBorder={false} className="shrink-0" />
        <div className="min-w-0 flex-1">
          <p
            className={cn(FRANKLIN_UPDATE_TEXT_CLASS_NAME, FRANKLIN_UPDATE_CLOSE_GUTTER_CLASS_NAME)}
          >
            {nudgeMessage(nudge, copy)}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={FRANKLIN_UPDATE_PRIMARY_ACTION_CLASS_NAME}
              onClick={onSummarise}
            >
              {copy.summarise}
            </button>
            {nudge.type === "new_issues" && (
              <button
                type="button"
                className={FRANKLIN_UPDATE_SECONDARY_ACTION_CLASS_NAME}
                onClick={onViewIssues}
              >
                {copy.viewIssues}
              </button>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={copy.dismiss}
        className={FRANKLIN_UPDATE_CLOSE_CLASS_NAME}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </FranklinSpeechBubble>
  );
}

ChatNudge.displayName = "ChatNudge";
